import { ChangeDetectionStrategy, Component, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SelectValueSyncDirective } from '../../../../../../../shared/directives/select-value-sync.directive';
import { WorkflowNodeConfigBase } from './node-config-base';
import { WorkflowConditionValueComponent } from './condition-value.component';
import {
  DEFAULT_WORKFLOW_TIMEZONE,
  WAIT_UNIT_OPTIONS,
  WORKFLOW_TIMEZONE_OPTIONS
} from './config-options';
import { WEEKDAY_LABELS, WEEKDAY_OPTIONS } from '../../../../models/workflow-wait.model';
import { getOutputBranchHandles } from '../../../models/node-catalog';
import { WorkflowPropertySelectComponent } from './property-select.component';
import { buildDatePropertyOptions, buildPropertyOptions } from '../../../models/workflow-variable-catalog';

/** Días laborables que el panel da por marcados cuando `window.days` todavía no existe. */
const DEFAULT_BUSINESS_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];

/**
 * WAIT — espera por tiempo / fecha / condición / horario laboral.
 *
 * Subtipos: FIXED_DURATION, UNTIL_DATETIME, UNTIL_CONDITION, BUSINESS_HOURS, PROPERTY_BASED,
 * EXIT_CONDITION. Portado 1:1 del panel viejo. Los branchKeys de UNTIL_CONDITION/EXIT_CONDITION
 * son PUERTOS del canvas (gotcha §6): al renombrarlos, las aristas que colgaban del puerto
 * anterior quedan como huérfanas y el canvas las conserva para que el usuario decida.
 */
@Component({
  selector: 'app-wf-wait-config',
  standalone: true,
  imports: [CommonModule, SelectValueSyncDirective, WorkflowPropertySelectComponent, WorkflowConditionValueComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @switch (subtype()) {
      @case ('FIXED_DURATION') {
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label [class]="labelClass">Duración</label>
            <input type="number" min="1" [class]="fieldClass" [value]="num('duration', 1)" [disabled]="readOnly()"
                   (change)="patchConfig({ duration: numberValue($event) })" />
          </div>
          <div>
            <label [class]="labelClass">Unidad</label>
            <select [class]="fieldClass" [value]="str('unit', 'DAYS')" [disabled]="readOnly()"
                    (change)="patchConfig({ unit: inputValue($event) })">
              @for (u of unitOptions; track u.value) { <option [value]="u.value">{{ u.label }}</option> }
            </select>
          </div>
        </div>
      }

      @case ('UNTIL_DATETIME') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Modo</label>
            <div class="flex gap-2">
              <button type="button" class="flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition"
                      [class]="udMode() === 'fixed' ? activeChoiceClass : idleChoiceClass"
                      [disabled]="readOnly()" (click)="setUdFixed()">Fecha fija</button>
              <button type="button" class="flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition"
                      [class]="udMode() === 'property' ? activeChoiceClass : idleChoiceClass"
                      [disabled]="readOnly()" (click)="setUdProperty()">Propiedad de fecha</button>
            </div>
          </div>
          @if (udMode() === 'fixed') {
            <div>
              <label [class]="labelClass">Fecha y hora</label>
              <input type="datetime-local" [class]="fieldClass" [value]="str('datetime')" [disabled]="readOnly()"
                     (change)="applyConfig({ datetime: inputValue($event) })" />
            </div>
          } @else {
            <div>
              <label [class]="labelClass">Propiedad de fecha</label>
              <app-wf-property-select
                [value]="str('dateProperty')" [options]="datePropertyOptions()" [disabled]="readOnly()"
                placeholder="Elige una propiedad de fecha…"
                (valueChange)="patchUdProperty({ dateProperty: $event })"></app-wf-property-select>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label [class]="labelClass">Desfase (días)</label>
                <input type="number" [class]="fieldClass" [value]="num('offsetDays', 0)" [disabled]="readOnly()"
                       (change)="patchUdProperty({ offsetDays: numberValue($event) })" />
              </div>
              <div>
                <label [class]="labelClass">Hora</label>
                <input type="time" [class]="fieldClass" [value]="str('time', '09:00')" [disabled]="readOnly()"
                       (change)="patchUdProperty({ time: inputValue($event) })" />
              </div>
            </div>
            <div>
              <label [class]="labelClass">Zona horaria</label>
              <!-- Desplegable y NO texto libre: un identificador IANA mal escrito revienta el
                   ZoneId.of() del backend y la espera nunca se programa. -->
              <select [class]="fieldClass" [value]="str('timezone', defaultTimezone)" [disabled]="readOnly()"
                      (change)="patchUdProperty({ timezone: inputValue($event) })">
                @for (tz of timezoneOptions; track tz) { <option [value]="tz">{{ tz }}</option> }
                @if (timezoneUnknown(str('timezone'))) {
                  <option [value]="str('timezone')">{{ str('timezone') }} (no listada)</option>
                }
              </select>
            </div>
          }
        </div>
      }

      @case ('UNTIL_CONDITION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Condición — propiedad</label>
            <app-wf-property-select
              [value]="nestedStr('condition', 'property')" [options]="propertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchNested('condition', { property: $event })"></app-wf-property-select>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Operador</label>
              <select [class]="fieldClass" [value]="nestedStr('condition', 'operator', 'EQUALS')" [disabled]="readOnly()"
                      (change)="patchNested('condition', { operator: inputValue($event) })">
                @for (o of operatorOptionsFor(propertyOptions(), nestedStr('condition', 'property'), nestedStr('condition', 'operator', 'EQUALS')); track o.value) {
                  <option [value]="o.value">{{ o.label }}</option>
                }
              </select>
            </div>
            <div>
              <label [class]="labelClass">Valor</label>
              <app-wf-condition-value
                [operator]="nestedStr('condition', 'operator', 'EQUALS')" [value]="nested('condition')['value']"
                [from]="nested('condition')['from']" [to]="nested('condition')['to']"
                [values]="nested('condition')['values']" [disabled]="readOnly()"
                [property]="nestedStr('condition', 'property')" [options]="propertyOptions()"
                (patch)="patchNested('condition', $event)" />
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Plazo máximo — duración</label>
              <input type="number" min="1" [class]="fieldClass" [value]="nestedNum('timeout', 'duration', 3)" [disabled]="readOnly()"
                     (change)="patchNested('timeout', { duration: numberValue($event) })" />
            </div>
            <div>
              <label [class]="labelClass">Unidad</label>
              <select [class]="fieldClass" [value]="nestedStr('timeout', 'unit', 'DAYS')" [disabled]="readOnly()"
                      (change)="patchNested('timeout', { unit: inputValue($event) })">
                @for (u of unitOptions; track u.value) { <option [value]="u.value">{{ u.label }}</option> }
              </select>
            </div>
          </div>
          <ng-container [ngTemplateOutlet]="branchSummary" />
        </div>
      }

      @case ('BUSINESS_HOURS') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Días laborables</label>
            <div class="flex flex-wrap gap-1.5">
              @for (day of weekdayOptions; track day) {
                <button type="button" class="rounded-lg border px-2.5 py-1 text-xs font-medium transition"
                        [class]="isDaySelected(day) ? activeChoiceClass : idleChoiceClass"
                        [disabled]="readOnly()" (click)="toggleDay(day)">{{ weekdayLabels[day] }}</button>
              }
            </div>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Desde</label>
              <input type="time" [class]="fieldClass" [value]="nestedStr('window', 'start', '08:00')" [disabled]="readOnly()"
                     (change)="patchNested('window', { start: inputValue($event) })" />
            </div>
            <div>
              <label [class]="labelClass">Hasta</label>
              <input type="time" [class]="fieldClass" [value]="nestedStr('window', 'end', '18:00')" [disabled]="readOnly()"
                     (change)="patchNested('window', { end: inputValue($event) })" />
            </div>
          </div>
          <div>
            <label [class]="labelClass">Zona horaria</label>
            <select [class]="fieldClass" [value]="nestedStr('window', 'timezone', defaultTimezone)" [disabled]="readOnly()"
                    (change)="patchNested('window', { timezone: inputValue($event) })">
              @for (tz of timezoneOptions; track tz) { <option [value]="tz">{{ tz }}</option> }
              @if (timezoneUnknown(nestedStr('window', 'timezone'))) {
                <option [value]="nestedStr('window', 'timezone')">{{ nestedStr('window', 'timezone') }} (no listada)</option>
              }
            </select>
          </div>
        </div>
      }

      @case ('PROPERTY_BASED') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Propiedad numérica</label>
            <app-wf-property-select
              [value]="str('numberProperty')" [options]="propertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchConfig({ numberProperty: $event })"></app-wf-property-select>
          </div>
          <div>
            <label [class]="labelClass">Unidad</label>
            <select [class]="fieldClass" [value]="str('unit', 'DAYS')" [disabled]="readOnly()"
                    (change)="patchConfig({ unit: inputValue($event) })">
              @for (u of unitOptions; track u.value) { <option [value]="u.value">{{ u.label }}</option> }
            </select>
          </div>
        </div>
      }

      @case ('EXIT_CONDITION') {
        <div class="space-y-4">
          <div>
            <label [class]="labelClass">Condición de salida — propiedad</label>
            <app-wf-property-select
              [value]="nestedStr('exitCondition', 'property')" [options]="propertyOptions()" [disabled]="readOnly()"
              placeholder="Elige una propiedad…"
              (valueChange)="patchNested('exitCondition', { property: $event })"></app-wf-property-select>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Operador</label>
              <select [class]="fieldClass" [value]="nestedStr('exitCondition', 'operator', 'EQUALS')" [disabled]="readOnly()"
                      (change)="patchNested('exitCondition', { operator: inputValue($event) })">
                @for (o of operatorOptionsFor(propertyOptions(), nestedStr('exitCondition', 'property'), nestedStr('exitCondition', 'operator', 'EQUALS')); track o.value) {
                  <option [value]="o.value">{{ o.label }}</option>
                }
              </select>
            </div>
            <div>
              <label [class]="labelClass">Valor</label>
              <app-wf-condition-value
                [operator]="nestedStr('exitCondition', 'operator', 'EQUALS')" [value]="nested('exitCondition')['value']"
                [from]="nested('exitCondition')['from']" [to]="nested('exitCondition')['to']"
                [values]="nested('exitCondition')['values']" [disabled]="readOnly()"
                [property]="nestedStr('exitCondition', 'property')" [options]="propertyOptions()"
                (patch)="patchNested('exitCondition', $event)" />
            </div>
          </div>
          <ng-container [ngTemplateOutlet]="branchSummary" />
          <!--
            Aquí había un «Revisar cada» (checkEvery) que el backend no lee en ninguna parte: la
            espera no se re-evalúa periódicamente, solo al vencer el plazo máximo. Configurarlo
            hacía creer que el registro saldría antes por la rama de salida.
          -->
          <p [class]="hintClass">
            La condición de salida se comprueba al vencer la espera máxima, no de forma periódica.
          </p>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label [class]="labelClass">Espera máxima — duración</label>
              <input type="number" min="1" [class]="fieldClass" [value]="nestedNum('maxWait', 'duration', 7)" [disabled]="readOnly()"
                     (change)="patchNested('maxWait', { duration: numberValue($event) })" />
            </div>
            <div>
              <label [class]="labelClass">Unidad</label>
              <select [class]="fieldClass" [value]="nestedStr('maxWait', 'unit', 'DAYS')" [disabled]="readOnly()"
                      (change)="patchNested('maxWait', { unit: inputValue($event) })">
                @for (u of unitOptions; track u.value) { <option [value]="u.value">{{ u.label }}</option> }
              </select>
            </div>
          </div>
        </div>
      }

      @default {
        <p class="rounded-lg bg-gray-50 p-3 text-xs text-gray-500 dark:bg-white/5 dark:text-gray-400">
          Este tipo de espera no tiene campos configurables. Usa el modo avanzado (JSON) si necesitas ajustarlo.
        </p>
      }
    }

    <!--
      INC_CRM_020 (8) inc44/inc46: antes había cuatro campos de texto ("Rama al cumplir",
      "Rama al vencer", "Rama de salida", "Rama de continuación") cuyo valor era la clave del
      puerto (DEFAULT, TIMEOUT, EXIT). Renombrarlas no aporta nada al usuario —el puerto solo
      existe para colgar la arista— y era la única forma de ver texto en inglés aquí, así que
      ahora se muestran como resumen legible de las salidas del paso.
    -->
    <ng-template #branchSummary>
      <div>
        <label [class]="labelClass">Salidas de este paso</label>
        <ul class="space-y-1.5 rounded-lg border border-gray-200 p-2.5 dark:border-gray-800">
          @for (branch of outputBranches(); track branch.branchKey) {
            <li class="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
              <span class="mt-1 h-2 w-2 shrink-0 rounded-full" [class]="branch.dotClass"></span>
              <span><span class="font-medium">{{ branch.label }}</span> — {{ branch.description }}</span>
            </li>
          }
        </ul>
        <p [class]="hintClass">Cada salida es un punto de conexión del lienzo: arrastra desde ella para enlazar el siguiente paso.</p>
      </div>
    </ng-template>
  `
})
export class WorkflowWaitConfigComponent extends WorkflowNodeConfigBase {
  protected readonly unitOptions = WAIT_UNIT_OPTIONS;
  protected readonly weekdayOptions = WEEKDAY_OPTIONS;
  protected readonly weekdayLabels = WEEKDAY_LABELS;
  protected readonly timezoneOptions = WORKFLOW_TIMEZONE_OPTIONS;
  protected readonly defaultTimezone = DEFAULT_WORKFLOW_TIMEZONE;

  /** Zona guardada que no está en la lista (viene del JSON crudo): se conserva como opción. */
  protected timezoneUnknown(timezone: string): boolean {
    return !!timezone && !WORKFLOW_TIMEZONE_OPTIONS.includes(timezone);
  }

  protected readonly activeChoiceClass = 'border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400';
  protected readonly idleChoiceClass =
    'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/5';

  protected readonly subtype = computed(() => this.node().nodeSubtype ?? '');

  constructor() {
    super();
    effect(() => this.seedDefaults());
  }

  /**
   * Persiste los valores que el panel PINTA por defecto.
   *
   * Cuatro de los seis tipos de espera nacen con la config vacía y `str()`/`nestedStr()` solo
   * aplican el valor por defecto AL MOSTRARLO: el usuario veía "Unidad: Días", "08:00–18:00" o
   * "Plazo máximo: 3 Días" y el nodo salía igualmente marcado "Configuración incompleta", porque
   * el backend exige justo esas claves (INVALID_WAIT_CONFIG). Peor aún en PROPERTY_BASED: como es
   * un `<select>`, volver a elegir "Días" no dispara `change`, así que ni siquiera había forma de
   * arreglarlo sin cambiar de unidad y volver.
   *
   * Se escribe lo mismo que se ve, ni un valor más: los días laborables L-V son los que ya pinta
   * `isDaySelected` cuando no hay `window.days`.
   */
  private seedDefaults(): void {
    const config = this.config();
    if (this.readOnly()) {
      return;
    }
    switch (this.subtype()) {
      case 'FIXED_DURATION':
        if (config['duration'] == null || config['unit'] == null) {
          this.patchConfig({ duration: this.num('duration', 1), unit: this.str('unit', 'DAYS') });
        }
        break;
      case 'PROPERTY_BASED':
        if (config['unit'] == null) {
          this.patchConfig({ unit: this.str('unit', 'DAYS') });
        }
        break;
      case 'BUSINESS_HOURS': {
        const win = this.nested('window');
        if (!Array.isArray(win['days']) || win['start'] == null || win['end'] == null) {
          this.patchNested('window', {
            days: Array.isArray(win['days']) ? win['days'] : DEFAULT_BUSINESS_DAYS,
            start: this.nestedStr('window', 'start', '08:00'),
            end: this.nestedStr('window', 'end', '18:00'),
            timezone: this.nestedStr('window', 'timezone', DEFAULT_WORKFLOW_TIMEZONE)
          });
        }
        break;
      }
      // El plazo SÍ se siembra (se ve relleno), pero la condición NO se crea de la nada: hacerlo
      // dejaría pasar la validación a un nodo sin propiedad que compararía nada y saldría siempre
      // por la rama de vencimiento. Solo se completa el operador que el panel ya muestra una vez
      // que el usuario ha empezado a rellenar la condición.
      case 'UNTIL_CONDITION':
        this.seedTimeout('timeout', config, 3);
        this.seedConditionOperator('condition', config);
        break;
      case 'EXIT_CONDITION':
        this.seedTimeout('maxWait', config, 7);
        this.seedConditionOperator('exitCondition', config);
        break;
      default:
        break;
    }
  }

  /** Persiste el plazo que el panel ya pinta (`timeout` de UNTIL_CONDITION, `maxWait` de EXIT_CONDITION). */
  private seedTimeout(key: string, config: Record<string, unknown>, defaultDuration: number): void {
    const current = this.nested(key);
    if (config[key] != null && current['duration'] != null && current['unit'] != null) {
      return;
    }
    this.patchNested(key, {
      duration: this.nestedNum(key, 'duration', defaultDuration),
      unit: this.nestedStr(key, 'unit', 'DAYS')
    });
  }

  /** Completa el operador de una condición YA empezada; nunca crea la condición entera. */
  private seedConditionOperator(key: string, config: Record<string, unknown>): void {
    if (config[key] == null || this.nested(key)['operator'] != null) {
      return;
    }
    this.patchNested(key, { operator: 'EQUALS' });
  }

  /** Puertos de salida del nodo, con su nombre en español (misma fuente que las tarjetas del lienzo). */
  protected readonly outputBranches = computed(() => getOutputBranchHandles(this.node()));

  /** Propiedades sugeridas del objeto base del workflow (etiqueta legible + clave). */
  protected readonly propertyOptions = computed(() => buildPropertyOptions(this.editor.workflow()?.objectType ?? null));

  /**
   * Solo las propiedades de FECHA. «Esperar hasta fecha» ofrecía la lista completa del objeto, así
   * que se podía elegir «Monto» como fecha de reanudación: en ejecución el valor no se parsea y la
   * inscripción queda esperando una fecha que no existe.
   */
  protected readonly datePropertyOptions = computed(() => buildDatePropertyOptions(this.editor.workflow()?.objectType ?? null));

  /** Modo de UNTIL_DATETIME: 'property' si hay dateProperty, 'fixed' en caso contrario. */
  protected readonly udMode = computed<'fixed' | 'property'>(() => (this.str('dateProperty') ? 'property' : 'fixed'));

  protected setUdFixed(): void {
    this.applyConfig({ datetime: this.str('datetime') });
  }

  protected setUdProperty(): void {
    this.applyConfig({
      dateProperty: this.str('dateProperty'),
      offsetDays: this.num('offsetDays', 0),
      time: this.str('time', '09:00'),
      timezone: this.str('timezone', 'America/Lima')
    });
  }

  /** En modo propiedad, mantiene SOLO las claves del modo propiedad (nunca datetime). */
  protected patchUdProperty(patch: Record<string, unknown>): void {
    this.applyConfig({
      dateProperty: this.str('dateProperty'),
      offsetDays: this.num('offsetDays', 0),
      time: this.str('time', '09:00'),
      timezone: this.str('timezone', 'America/Lima'),
      ...patch
    });
  }

  protected isDaySelected(day: string): boolean {
    const days = this.nested('window')['days'];
    const list = Array.isArray(days) ? (days as string[]) : DEFAULT_BUSINESS_DAYS;
    return list.includes(day);
  }

  protected toggleDay(day: string): void {
    const days = this.nested('window')['days'];
    const list = Array.isArray(days) ? [...(days as string[])] : [...DEFAULT_BUSINESS_DAYS];
    const next = list.includes(day) ? list.filter((d) => d !== day) : [...list, day];
    this.patchNested('window', { days: next });
  }
}
